import { FunctionComponent, useState } from 'react'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { ChildParent } from '@/types/Child'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/modules/context/AuthContext'
import styles from './adultTable.module.scss'
import { Role } from '@/types/Role'
import Title from '@/components/_custom/Title/title'
import AdultTableRow from './adultTableRow'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { Add } from '@mui/icons-material'
import AddParentToChildDialog from './Dialog/addParentToChildDialog'
import Desktop from '@/modules/desktopMobile/desktop'
import { useScreenSize } from '@/modules/desktopMobile/screenSizeContext'

interface AdultTableProps {
    parents: ChildParent[]
    daycareId: number
    childId: number
    childName: string
    handleRefetchChild: () => void
    isRpeOrLaep: boolean
}

const AdultTable: FunctionComponent<AdultTableProps> = ({
    parents,
    childId,
    childName,
    daycareId,
    handleRefetchChild,
    isRpeOrLaep
}: AdultTableProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const [addParentToChildDialog, setAddParentToChildDialog] = useState(false)

    const isMobile: boolean = useScreenSize()

    return (
        <>
            <div className={styles.TitleAndSelector}>
                <Title text={isRpeOrLaep ? t('publicInformation') : t('parentInformation')} className={styles.Title} />
                {user?.currentDaycareRights.canWriteFamilies && (
                    <ButtonTextIcon
                        type={isMobile ? 'add' : 'custom'}
                        customText={t('addAdult')}
                        customIcon={<Add />}
                        onClick={() => setAddParentToChildDialog(true)}
                        className={styles.Button}
                        disabled={!user.currentDaycareRights.canWriteFamilies}
                    />
                )}
            </div>
            {user && (
                <TableContainer className={styles.TablePlacement}>
                    <Table className={styles.Table}>
                        <TableHead className={styles.TableHeader}>
                            <TableRow>
                                <Desktop>
                                    <TableCell sx={{ width: '5%' }} />
                                    <TableCell>{t('firstname')}</TableCell>
                                </Desktop>
                                <TableCell>{t('lastname')}</TableCell>
                                <Desktop>
                                    <TableCell>{t('email')}</TableCell>
                                    <TableCell>{t('phone')}</TableCell>
                                </Desktop>
                                {user.currentDaycareRights.canReadOccasionalSlot && user.role !== Role.parent && (
                                    <TableCell align={'center'} sx={{ width: isMobile ? '30%' : '15%' }}>
                                        {t('requestSlot')}
                                    </TableCell>
                                )}
                                <TableCell align={'center'} sx={{ width: isMobile ? '30%' : '15%' }}>
                                    {t('actions')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        {parents.length === 0 ? (
                            <TableBody>
                                <TableRow>
                                    <TableCell colSpan={6}>{t('noAdults')}</TableCell>
                                </TableRow>
                            </TableBody>
                        ) : (
                            [...parents]
                                .sort((parentA: ChildParent, parentB: ChildParent) => {
                                    return parentA.lastname.localeCompare(parentB.lastname)
                                })
                                .map((parent: ChildParent) => (
                                    <AdultTableRow
                                        parent={parent}
                                        key={parent.parentId + childId + daycareId}
                                        childId={childId}
                                        daycareId={daycareId}
                                        handleRefetchChild={handleRefetchChild}
                                    />
                                ))
                        )}
                    </Table>
                </TableContainer>
            )}
            {addParentToChildDialog && (
                <AddParentToChildDialog
                    open={addParentToChildDialog}
                    handleClose={() => setAddParentToChildDialog(false)}
                    childId={childId}
                    daycareId={daycareId}
                    parents={parents}
                    childName={childName}
                    handleRefetchChild={handleRefetchChild}
                />
            )}
        </>
    )
}

export default AdultTable
